import { Component, OnInit, Input, OnChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ReminderService } from './reminder.service';
import { Subscription } from 'rxjs';
import { MultiSelect } from 'primeng/multiselect';


@Component({
    selector: 'app-reminder',
    templateUrl: './reminder.component.html',
    styleUrls: ['./reminder.component.scss'],
    standalone: false
})
export class ReminderComponent implements OnInit, OnChanges {
  @ViewChild('multiSelect') multiSelect!: MultiSelect;

  @Input() taskId: number | null = null;
  @Input() projectId: number | null = null;

  selectedUsers: any[] = [];
  showUsersDialog = false;

  reminderForm: UntypedFormGroup = new UntypedFormGroup({
    title: new UntypedFormControl('', [Validators.required]),
    notes: new UntypedFormControl('', [Validators.required]),
    recipientUserIds: new UntypedFormControl([], [Validators.required]),
    reminderDate: new UntypedFormControl(null, [Validators.required]),
    reminderEndDate: new UntypedFormControl(null, [Validators.required]),
    reminderTime: new UntypedFormControl(new Date(new Date().setHours(0, 0, 0, 0)), [Validators.required]),
    recurrenceType: new UntypedFormControl('NONE'),
    taskId: new UntypedFormControl(null),
    projectId: new UntypedFormControl(null),
    cronExpression: new UntypedFormControl(null)
  });

  usersList: any[] = [];
  reminders: any[] = [];
  minDate = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
  );

  recurrenceOptions = [
    { label: 'One-Time', value: 'NONE' },
    { label: 'Daily', value: 'DAILY' },
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Monthly', value: 'MONTHLY' }
  ];
  loadingReminders = false;
  loadingUsers = false;
  private remindersSubscription?: Subscription;
  editingReminder: any = null;
  showDeleteConfirm: boolean = false;
  deleteReminderTarget: any = null;

  constructor(private reminderService: ReminderService) { }

  ngOnInit(): void {
    this.loadUsers();
    this.loadReminders();
    // Listen for recurrenceType changes to update End Date field
    this.reminderForm.get('recurrenceType')?.valueChanges.subscribe((val) => {
      if (val === 'NONE') {
        this.reminderForm.get('reminderEndDate')?.disable();
        this.reminderForm.get('reminderEndDate')?.clearValidators();
        this.reminderForm.get('reminderEndDate')?.updateValueAndValidity();
      } else {
        this.reminderForm.get('reminderEndDate')?.enable();
        this.reminderForm.get('reminderEndDate')?.setValidators([Validators.required]);
        this.reminderForm.get('reminderEndDate')?.updateValueAndValidity();
      }
    });
    // Set initial state for End Date
    if (this.reminderForm.get('recurrenceType')?.value === 'NONE') {
      this.reminderForm.get('reminderEndDate')?.disable();
      this.reminderForm.get('reminderEndDate')?.clearValidators();
      this.reminderForm.get('reminderEndDate')?.updateValueAndValidity();
    }
  }

  ngOnChanges(): void {
    this.loadReminders();
  }

  loadUsers() {
    this.loadingUsers = true;
    this.reminderService.getUsers({}).subscribe(users => {
      this.usersList = users.data;
      this.loadingUsers = false;
    }, () => { this.loadingUsers = false; });
  }

  loadReminders() {
    if (this.remindersSubscription) {
      this.remindersSubscription.unsubscribe();
    }
    this.loadingReminders = true;
    this.remindersSubscription = this.reminderService.getReminders(this.taskId, this.projectId).subscribe((reminders: any) => {
      this.reminders = reminders.data;
      this.loadingReminders = false;
    }, () => { this.loadingReminders = false; });
  }

  getUserEmail(id: number) {
    const user = this.usersList.find(u => u.id === id);
    return user ? user.mainEmail : id;
  }

  getReminderLabel(recurrenceType: string) {
    const option = this.recurrenceOptions.find(opt => opt.value === recurrenceType);
    return option ? option.label : null;
  }

  onEdit(reminder: any) {
    this.editingReminder = reminder;
    this.reminderForm.patchValue({
      title: reminder.title,
      notes: reminder.notes,
      recipientUserIds: reminder.recipientUserIds,
      reminderDate: new Date(reminder.reminderDate),
      reminderTime: new Date(reminder.reminderDate),
      reminderEndDate: new Date(reminder.reminderEndDate),
      recurrenceType: reminder.recurrenceType,
      taskId: reminder.taskId,
      projectId: reminder.projectId,
      cronExpression: reminder.cronExpression
    });
    // Set End Date field state after patch
    if (reminder.recurrenceType === 'NONE') {
      this.reminderForm.get('reminderEndDate')?.disable();
      this.reminderForm.get('reminderEndDate')?.clearValidators();
      this.reminderForm.get('reminderEndDate')?.updateValueAndValidity();
    } else {
      this.reminderForm.get('reminderEndDate')?.enable();
      this.reminderForm.get('reminderEndDate')?.setValidators([Validators.required]);
      this.reminderForm.get('reminderEndDate')?.updateValueAndValidity();
    }
  }

  onDelete(reminder: any) {
    this.deleteReminderTarget = reminder;
    this.showDeleteConfirm = true;
  }

  onDeleteConfirmed(confirmed: any) {
    if (confirmed == 'Yes' && this.deleteReminderTarget) {
      this.reminderService.deleteReminder(this.deleteReminderTarget.id).subscribe(() => {
        this.loadReminders();
        this.deleteReminderTarget = null;
        this.showDeleteConfirm = false;
      });
    } else {
      this.deleteReminderTarget = null;
      this.showDeleteConfirm = false;
    }
  }

  onSubmit() {
    if (this.reminderForm.invalid) return;
    const form = this.reminderForm.value;
    const date: Date = form.reminderDate;
    const time: Date = form.reminderTime;
    const reminderDate = new Date(
      date.getFullYear(), date.getMonth(), date.getDate(),
      time.getHours(), time.getMinutes(), 0, 0
    );
    let reminderEndDate = null;
    if (this.reminderForm.get('recurrenceType')?.value !== 'NONE') {
      reminderEndDate = new Date(
        form.reminderEndDate.getFullYear(), form.reminderEndDate.getMonth(), form.reminderEndDate.getDate(),
        time.getHours(), time.getMinutes(), 0, 0
      );
    }

    const payload = {
      title: form.title,
      notes: form.notes,
      recipientUserIds: form.recipientUserIds,
      taskId: this.taskId,
      projectId: this.projectId,
      reminderDate: this.formatLocalDateTime(reminderDate),
      reminderEndDate: reminderEndDate ? this.formatLocalDateTime(reminderEndDate) : null,
      recurrenceType: form.recurrenceType,
      cronExpression: form.cronExpression
    };
    if (this.editingReminder) {
      this.reminderService.updateReminder(this.editingReminder.id, payload).subscribe(() => {
        this.loadReminders();
        this.resetForm();
      });
    } else {
      this.reminderService.createReminder(payload).subscribe(() => {
        this.loadReminders();
        this.resetForm();
      });
    }
  }

  resetForm() {
    this.reminderForm.reset({
      title: '',
      notes: '',
      recipientUserIds: [],
      reminderDate: null,
      reminderTime: new Date(new Date().setHours(0, 0, 0, 0)),
      recurrenceType: 'NONE',
      taskId: null,
      projectId: null,
      cronExpression: null
    });
    this.editingReminder = null;
    // Reset End Date field state
    this.reminderForm.get('reminderEndDate')?.disable();
    this.reminderForm.get('reminderEndDate')?.clearValidators();
    this.reminderForm.get('reminderEndDate')?.updateValueAndValidity();
    
    // 🔥 PRIME FIX FOR MULTISELECT FILTER (PrimeNG 11)
    if (this.multiSelect) {
      // 1️⃣ Force overlay to close
      this.multiSelect.hide();

      // 2️⃣ Clear internal filter state
      this.multiSelect.filterValue = '';
      (this.multiSelect as any)._filteredOptions = null;

      // 3️⃣ Force PrimeNG to refresh view
      setTimeout(() => {
        this.multiSelect.filterValue = '';
        (this.multiSelect as any)._filteredOptions = null;
      });
    }
  }

  onMultiSelectHide() {
    if (this.multiSelect) {
      this.multiSelect.filterValue = '';
      (this.multiSelect as any)._filteredOptions = null;
    }
  }

  formatLocalDateTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  // Add this method inside your component class
  showUsers() {

    const selectedIds = this.reminderForm.get('recipientUserIds')?.value;
    this.selectedUsers = this.usersList
      .filter(user => selectedIds.includes(user.id));
    if (this.selectedUsers.length > 0) {
      this.showUsersDialog = true;
    }
  }

  // Inside your component class
  get hasSelectedUsers(): boolean {
      const selected = this.reminderForm.get('recipientUserIds')?.value;
      return selected && selected.length > 0;
  }
}
